// Shared shell widgets: top bar, pills, toasts.

import { h, onLongPress } from '@kit/dom';
import { getSave, subscribe, totalStars } from './state';
import { go } from './router';
import { art, onArtReady } from '@kit/assets';
import { portraitSvg, avatarEl } from '@avatar/render';
import { canPaint } from '@avatar/painted';

export function gemPill(): HTMLElement {
  const n = h('span', null, String(getSave().gems));
  const el = h('span.pill', { title: 'Gems' }, h('span.dot.gem'), n);
  const un = subscribe(s => { n.textContent = String(s.gems); });
  (el as any)._un = un;
  return el;
}

export function starPill(): HTMLElement {
  const n = h('span', null, String(totalStars()));
  const el = h('span.pill', { title: 'Stars' }, h('span.dot.star'), n);
  const un = subscribe(s => { n.textContent = String(totalStars(s)); });
  (el as any)._un = un;
  return el;
}

export interface BarOpts { back?: string | (() => void); longPress?: () => void; pills?: boolean }

export function topBar(title: string, opts: BarOpts = {}): HTMLElement {
  const t = h('div.title', null, title);
  if (opts.longPress) onLongPress(t, opts.longPress);
  const kids: HTMLElement[] = [];
  if (opts.back !== undefined) {
    kids.push(h('button.btn.icon.ghost', {
      'aria-label': 'Back',
      onclick: () => typeof opts.back === 'function' ? opts.back() : go(opts.back as string),
    }, backIcon()));
  }
  kids.push(t);
  if (opts.pills !== false) { kids.push(avatarButton()); kids.push(starPill()); kids.push(gemPill()); }
  return h('div.bar', null, ...kids);
}

/** Her face in the top bar: one tap to the Studio from anywhere. */
export function avatarButton(): HTMLElement {
  const b = h('button', {
    'aria-label': 'Change my look',
    title: 'Change my look',
    onclick: () => go('studio'),
    style: { width: '40px', height: '40px', minWidth: '40px', borderRadius: '50%', border: '2px solid var(--line)', background: 'var(--surface)', padding: '0', overflow: 'hidden', display: 'flex', alignItems: 'flex-start', justifyContent: 'center' },
  });
  const paint = () => {
    const a = getSave().avatar;
    b.innerHTML = '';
    // painted face when the art is loaded, otherwise the SVG head and shoulders
    if (canPaint(a)) b.append(avatarEl(a, 40, 'face'));
    else b.innerHTML = portraitSvg(a, 56).replace('<svg ', '<svg style="margin-top:-2px" ');
  };
  paint();
  (b as any)._un = subscribe(paint);
  let t = 0;
  onArtReady(() => { clearTimeout(t); t = window.setTimeout(() => { if (b.isConnected) paint(); }, 200); });
  return b;
}

export function backIcon(): HTMLElement {
  const s = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  s.setAttribute('viewBox', '0 0 24 24'); s.setAttribute('width', '26'); s.setAttribute('height', '26');
  s.innerHTML = '<path d="M15 5 L8 12 L15 19" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>';
  return s as unknown as HTMLElement;
}

let toastEl: HTMLElement | null = null;
let toastTimer = 0;
export function toast(text: string, ms = 2200) {
  if (!toastEl) {
    toastEl = h('div', { style: { position: 'fixed', left: '50%', bottom: '24px', transform: 'translateX(-50%)', background: 'var(--text)', color: 'var(--bg)', padding: '10px 16px', borderRadius: '999px', fontWeight: '800', zIndex: '100', maxWidth: '90vw', textAlign: 'center' } });
    document.body.appendChild(toastEl);
  }
  toastEl.textContent = text;
  toastEl.style.display = 'block';
  clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => { if (toastEl) toastEl.style.display = 'none'; }, ms);
}

/** A painted banner for a screen header, or nothing when the art is not there yet. */
export function banner(id: string): HTMLElement | null {
  const img = art(id);
  if (!img) return null;
  const el = new Image();
  el.src = img.src;
  el.alt = '';
  el.style.cssText = 'display:block;width:100%;aspect-ratio:3/1;object-fit:cover;object-position:center 60%;border-radius:14px;border:2px solid var(--line);';
  return h('div', { style: { padding: '0 16px 10px' } }, el);
}

export function categoryColor(c: string): string {
  return c === 'words' ? 'var(--words)' : c === 'math' ? 'var(--math)' : c === 'science' ? 'var(--science)' : 'var(--fun)';
}

export function categoryLabel(c: string): string {
  return c === 'words' ? 'Words' : c === 'math' ? 'Math' : c === 'science' ? 'Science' : 'Fun';
}
